/**
 * Sophia, the phone receptionist: who answers, how she sounds and how she talks. Everything here is
 * a default the owner can change in Settings → Messages & calls without touching code.
 */

/** Female voices she can use (ElevenLabs, through Twilio ConversationRelay; no extra subscription). */
export const SOPHIA_VOICES: { id: string; label: string }[] = [
  { id: 'ElevenLabs:cgSgspJ2msm6clMCkdW9', label: 'Jessica · warm, bright, a little playful (American)' },
  { id: 'ElevenLabs:EXAVITQu4vr4xnSDxMaL', label: 'Sarah · calm, confident, professional (American)' },
  { id: 'ElevenLabs:FGY2WhTYpPnrIDTdsKH5', label: 'Laura · upbeat and sunny (American)' },
  { id: 'ElevenLabs:XrExE9yKIg1WjnnlVkGX', label: 'Matilda · warm and professional (American)' },
  { id: 'ElevenLabs:Xb7hH8MSUJpSbSDYk0k2', label: 'Alice · friendly and clear (British)' },
  { id: 'Google:en-US-Journey-O', label: 'Google Journey O (woman)' },
];

/** ElevenLabs models: Flash is the fastest; Turbo sounds a little richer and takes a little longer. */
export const SOPHIA_VOICE_MODELS = ['flash_v2_5', 'turbo_v2_5'] as const;

/**
 * How she hears callers (speech to text). Deepgram Nova-3 "multi" understands English and Spanish
 * (and callers switching between them) by itself; Arabic is switched to when the caller speaks it.
 */
export const SOPHIA_HEARING = {
  deepgram_multi: 'Deepgram Nova-3: English and Spanish by itself (best)',
  deepgram_en: 'Deepgram Nova-3: English only',
  google: 'Google: English only (the phone agent before her)',
} as const;
export type SophiaHearing = keyof typeof SOPHIA_HEARING;

/** The Arabic she listens for once a caller speaks Arabic (Deepgram has no Libyan code; generic Arabic or Tunisian are closest). */
export const SOPHIA_ARABIC_CODES: { code: string; label: string }[] = [
  { code: 'ar', label: 'Arabic (general, Modern Standard)' },
  { code: 'ar-TN', label: 'Arabic, Tunisian (closest to Libyan)' },
  { code: 'ar-EG', label: 'Arabic, Egyptian' },
  { code: 'ar-SA', label: 'Arabic, Saudi (Gulf)' },
  { code: 'ar-AE', label: 'Arabic, Emirati (Gulf)' },
  { code: 'ar-JO', label: 'Arabic, Jordanian (Levantine)' },
  { code: 'ar-LB', label: 'Arabic, Lebanese (Levantine)' },
  { code: 'ar-SY', label: 'Arabic, Syrian (Levantine)' },
  { code: 'ar-IQ', label: 'Arabic, Iraqi' },
  { code: 'ar-MA', label: 'Arabic, Moroccan' },
  { code: 'ar-DZ', label: 'Arabic, Algerian' },
];

export const SOPHIA_DEFAULTS = {
  // off: everyone keeps the old assistant. test: only the numbers in sophia_test_numbers get
  // Sophia. all: every caller.
  sophia_mode: 'test',
  sophia_test_numbers: '',
  sophia_name: 'Sophia',
  sophia_voice: 'ElevenLabs:cgSgspJ2msm6clMCkdW9',
  sophia_voice_model: 'flash_v2_5',
  // ElevenLabs: speed 0.7–1.2, stability and similarity 0–1. A touch quicker than default, with
  // enough variation to sound alive.
  sophia_voice_speed: '1.0',
  sophia_voice_stability: '0.5',
  sophia_voice_similarity: '0.75',
  sophia_personality: [
    'You are {name}, the receptionist at {store}. You sound like a woman in her late twenties who has worked at the counter for years: warm, friendly, confident and quick, and you like helping people.',
    'You know phones, repairs, buying and selling phones, and wireless plans really well, and you explain them in simple words.',
    'You are occasionally funny when the caller is joking around, calm and patient when they are frustrated, and professional when it matters.',
    'You are helpful and naturally persuasive without ever being pushy.',
    'Match the caller’s energy: relaxed with relaxed callers, quick and to the point with people in a hurry, gentle with upset callers, slower and clearer with older callers.',
    'You talk like a real person at a busy phone store, never like a script or a website: short, casual, to the point.',
  ].join('\n'),
  // How she hears callers, and which Arabic she listens for when a caller speaks Arabic.
  sophia_hearing: 'deepgram_multi',
  sophia_arabic_code: 'ar',
  // The first thing callers hear, one per line, taken in turn. {store} and {name} are filled in.
  // When calls are recorded, "this call may be recorded" is added before the question.
  sophia_greetings: [
    'Hey! Thanks for calling {store}, this is {name}. What can I help you with?',
    'Hi there! You’ve reached {store}, {name} speaking. How can I help?',
    'Thanks for calling {store}! This is {name}. What’s going on?',
  ].join('\n'),
  // Ways she ends a call, one per line (she varies them; these are examples, not a script).
  sophia_goodbyes: [
    'Sounds good! Thanks for calling, and have a great day.',
    'Perfect. Take care!',
    'Awesome! We look forward to seeing you.',
    'Thanks for calling {store}. Bye now!',
  ].join('\n'),
  // Where she transfers a caller who asks for a person (while the store is open). Not the store
  // number that forwards to her, or the call would come right back.
  voice_transfer_number: '',
};

/** Whole lines, without blanks. */
export function settingLines(text: string): string[] {
  return (text ?? '').split('\n').map((l) => l.trim()).filter(Boolean);
}

/** The ElevenLabs voice setting for ConversationRelay: voiceId-model-speed_stability_similarity. */
export function elevenLabsVoice(id: string, model: string, speed: string, stability: string, similarity: string): string {
  return `${id}-${model}-${speed}_${stability}_${similarity}`;
}
